
import Orderline from "@/types/Orderline";
import {
  defineComponent,
  PropType,
  computed,
  ref,
  watch,
  onMounted,
} from "vue";
import Button from "../Buttons/Button.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import {
  mdiClose,
  mdiDeleteForever,
  mdiLoading,
  mdiMinus,
  mdiPencil,
  mdiPlus,
  mdiTruck,
  mdiMinusCircle,
} from "@mdi/js";
import { useForm } from "@inertiajs/inertia-vue3";
import { debounce } from "lodash";
import Modal from "../General/Modal.vue";
import { useStore } from "@/stores/order";
import TextButton from "../Buttons/TextButton.vue";
import ValidationFormErrors from "../Forms/ValidationFormErrors.vue";

export default defineComponent({
  props: {
    orderline: {
      type: Object as PropType<Orderline>,
      required: true,
    },
  },
  setup(props) {
    const orderStore = useStore();
    const changeAmount = (amountChange: number) => {
      editForm.value.amount += amountChange;
      // If the form is submitted, we cancel to avoid number jumping back
      editForm.value.cancel();

      sendEditForm();
    };

    const totalPrice = computed(() => {
      return props.orderline.amount * props.orderline.price_out_with_mva;
    });

    const toCurrency = (price: number) => {
      const formattedPrice = price.toLocaleString("nb", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      
      });
      return `kr ${formattedPrice}`;
    };

    const showEditModal = ref(false);

    const editForm = ref(
      useForm({
        _method: "PUT",
        amount: props.orderline.amount,
        price_out: props.orderline.price_out,
        comment: props.orderline.comment,
      })
    );

    const destroyLoading = ref(false);

    const destroyOrderline = () => {
      orderStore.actionsLocked = true;
      destroyLoading.value = true;

      editForm.value._method = "DELETE";
      editForm.value.post(
        route("orders.orderlines.destroy", {
          order: props.orderline.order_id,
          orderline: props.orderline.id,
        }),
        {
          preserveScroll: true,
          onFinish: () => {
            orderStore.actionsLocked = false;
            destroyLoading.value = false;
          },
        }
      );

      editForm.value._method = "PATCH";
    };

    const sendEditForm = debounce(() => {
      orderStore.actionsLocked = true;

      editForm.value.post(
        route("orders.orderlines.update", {
          order: props.orderline.order_id,
          orderline: props.orderline.id,
        }),
        {
          onSuccess: (page) => {
            showEditModal.value = false;
          },
          onFinish: () => {
            orderStore.actionsLocked = false;
          },
          preserveScroll: true,
        }
      );
    }, 150);

    const abortEdit = () => {
      editForm.value.reset();
      showEditModal.value = false;
    };

    const mvaMultiplier = computed(() => {
      return 1 + props.orderline.product.mva_percent / 100;
    });

    const mvaUnmultiplier = computed(() => {
      return 1 / mvaMultiplier.value;
    });

    const editRebate = computed(
      () =>
        +(
          ((props.orderline.product.price - editForm.value.price_out) /
            props.orderline.product.price) *
          100
        ).toFixed(2)
    );

    const setEditRebate = (event: Event) => {
      if (!event.target) {
        return;
      }

      const value = parseFloat((event.target as HTMLInputElement).value);

      editForm.value.price_out =
        Math.round(
          props.orderline.product.price *
            mvaMultiplier.value *
            ((100 - value) / 100)
        ) * mvaUnmultiplier.value;
    };

    const setPriceOutWithMva = (event: Event) => {
      if (!event.target) {
        return;
      }

      const value = parseFloat((event.target as HTMLInputElement).value);
      editForm.value.price_out = value * mvaUnmultiplier.value;
    };

    const priceOutWithMva = computed(() => {
      return (editForm.value.price_out * mvaMultiplier.value).toFixed(2);
    });

    const discount = computed(() => {
      if (props.orderline.product.price === 0) {
        return 0;
      }

      return Math.round(
        ((props.orderline.price_out - props.orderline.product.price) /
          props.orderline.product.price) *
          100
      );
    });

    const paymentProcessing = computed(() => orderStore.paymentProcessing);

    const orderlineIsEditable = computed(() => {
      return (
        orderStore.isEditable &&
        !destroyLoading.value &&
        !paymentProcessing.value
      );
    });

    const isWeborder = computed(() => orderStore.isWeborder);

    const productLink = computed(() => {
      return `/products/view-product/${props.orderline.product.id}`;
    });

    const showProductImageModal = ref(false);

    const showImage = () => {
      if (props.orderline.product.main_image?.url) {
        showProductImageModal.value = true;
      }
    };

    const selectText = (e: FocusEvent) => {
      if (e.target instanceof HTMLInputElement) {
        e?.target?.select();
      }
    };

    const resetEditForm = () => {
      editForm.value = useForm({
        _method: "PUT",
        amount: props.orderline.amount,
        price_out: props.orderline.price_out,
        comment: props.orderline.comment,
      });
    };

    const openEditModal = () => {
      resetEditForm();

      showEditModal.value = true;
    };

    const unscanProduct = (barcode: string = "") => {
      orderStore.scanProduct(barcode, 1, "unscan");
    };
    const toggleCheckKgOrder = (e: any, product_id: number) => {
      const action = e.target.checked ? "check" : "uncheck";
      const quantity = e.target.checked ? 1 : 0;
      orderStore.scanKGProduct(product_id, quantity, action);
    };

    watch(
      () => props.orderline,
      (value) => {
        editForm.value.amount = value.amount;
        editForm.value.price_out = value.price_out;
        editForm.value.comment = value.comment;
      },
      { deep: true }
    );

    return {
      productLink,
      selectText,
      discount,
      editForm,
      editRebate,
      priceOutWithMva,
      setPriceOutWithMva,
      abortEdit,
      sendEditForm,
      setEditRebate,
      totalPrice,
      toCurrency,
      changeAmount,
      showEditModal,
      isWeborder,
      showImage,
      mvaMultiplier,
      mvaUnmultiplier,
      openEditModal,
      orderlineIsEditable,
      destroyOrderline,
      destroyLoading,
      showProductImageModal,
      editIcon: mdiPencil,
      plusIcon: mdiPlus,
      minusIcon: mdiMinus,
      loadingIcon: mdiLoading,
      shippingIcon: mdiTruck,
      closeIcon: mdiClose,
      deleteIcon: mdiDeleteForever,
      closed: computed(() => orderStore.isClosed),
      actionsLocked: computed(() => orderStore.actionsLocked),
      paymentProcessing,
      unscanProduct,
      minusCircle: mdiMinusCircle,
      toggleCheckKgOrder,
    };
  },
  components: { Button, SvgIcon, Modal, TextButton, ValidationFormErrors },
});
